/**
 * InspectionPhotoGrid — 2x3 grid of photo slots for vehicle inspection.
 *
 * Each slot opens the in-app live camera. External media picker is not allowed
 * for insurance evidence.
 */
import { useState } from 'react'
import { View, Text, Image } from 'react-native'
import { Pressable } from '@/components/ui/Pressable'
import { selection } from '@/lib/utils/haptics'
import { Camera, RefreshCw } from '@/components/ui/icons'
import { useTranslation } from 'react-i18next'
import { COLORS, FONTS, SPACING, RADIUS } from '@/lib/brand'
import { useSemanticColors } from '@/lib/theme/tokens'
import { useTheme } from '@/lib/hooks/use-theme'
import { InspectionLiveCamera } from './InspectionLiveCamera'
import {
  isInspectionPhotoRequired,
  type InspectionPhotoArea,
  type InspectionPhoto,
} from '@/lib/types/inspection.types'

/**
 * Que se vea CUALES hay que tomar.
 *
 * La cuadricula ofrece las quince areas, pero solo seis bloquean la entrega. Si
 * las quince se ven iguales, la persona no sabe cuales importan y toma quince
 * --la friccion que estamos quitando-- o seis al azar y no puede enviar. La
 * marca "Opcional" va solo sobre tomas que no bloquean. La licencia no es una
 * foto del carro, pero cuando se muestra en pickup sí bloquea la entrega;
 * llamarla opcional contradice el CTA y hace que la gente la deje para después.
 */
interface InspectionPhotoGridProps {
  photos: InspectionPhoto[]
  onPhotoTaken: (photo: InspectionPhoto) => void
  /**
   * Alternar "esta foto muestra un daño".
   *
   * Opcional: la grilla se usa también donde no hay nada que reportar, y una
   * pantalla que no la pasa simplemente no ofrece la marca en vez de tener un
   * botón que no hace nada.
   */
  onToggleDamage?: (area: InspectionPhotoArea) => void
  areas: readonly InspectionPhotoArea[]
  testIDPrefix?: string
}

const AREA_LABEL_KEYS: Record<InspectionPhotoArea, string> = {
  // La licencia no se dibuja en esta cuadricula --es un paso aparte-- pero el
  // mapa cubre el tipo completo para que agregar un area en el futuro rompa
  // aca, en tiempo de compilacion, y no en pantalla con una etiqueta vacia.
  license: 'checkin.photo_license',
  front: 'checkin.photo_front',
  front_left: 'checkin.photo_front_left',
  front_right: 'checkin.photo_front_right',
  rear_left: 'checkin.photo_rear_left',
  back: 'checkin.photo_back',
  rear_right: 'checkin.photo_rear_right',
  left: 'checkin.photo_left',
  right: 'checkin.photo_right',
  windshield: 'checkin.photo_windshield',
  odometer: 'checkin.photo_odometer',
  front_seats: 'checkin.photo_front_seats',
  rear_seats: 'checkin.photo_rear_seats',
  dashboard: 'checkin.photo_dashboard',
  trunk: 'checkin.photo_trunk',
  tires: 'checkin.photo_tires',
}

export function InspectionPhotoGrid({
  photos,
  onPhotoTaken,
  onToggleDamage,
  areas,
  testIDPrefix = 'checkin',
}: InspectionPhotoGridProps) {
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const { t } = useTranslation()
  const [activeArea, setActiveArea] = useState<InspectionPhotoArea | null>(null)

  const handleSlotPress = (area: InspectionPhotoArea) => {
    void selection()
    setActiveArea(area)
  }

  const getPhotoForArea = (area: InspectionPhotoArea) => photos.find((p) => p.area === area)

  return (
    <View style={{ gap: SPACING.sm }}>
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: SPACING.sm }}>
        {areas.map((area) => {
          const photo = getPhotoForArea(area)
          const label = t(AREA_LABEL_KEYS[area])
          const isRequired = isInspectionPhotoRequired(area)

          return (
            <Pressable
              key={area}
              onPress={() => handleSlotPress(area)}
              onLongPress={photo && onToggleDamage ? () => onToggleDamage(area) : undefined}
              accessibilityRole="button"
              accessibilityLabel={
                photo
                  ? `${label} - ${t('checkin.retake_photo')}${
                      photo.isDamage ? ` - ${t('inspection.damage_marked')}` : ''
                    }`
                  : `${t('checkin.add_photo')} - ${label}${
                      isRequired ? '' : ` (${t('checkin.photo_optional')})`
                    }`
              }
              accessibilityHint={photo && onToggleDamage ? t('inspection.mark_damage') : undefined}
              testID={`${testIDPrefix}.photo-${area}`}
              style={{
                width: '48%',
                aspectRatio: 4 / 3,
                borderRadius: RADIUS.md,
                overflow: 'hidden',
                backgroundColor: colors.surfaceSubtle,
                borderWidth: 1,
                borderColor: colors.border,
                borderStyle: 'dashed',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {photo ? (
                <View style={{ width: '100%', height: '100%' }}>
                  <Image
                    source={{ uri: photo.uri }}
                    style={{ width: '100%', height: '100%' }}
                    resizeMode="cover"
                  />
                  {/* El daño se ve sobre la foto que lo muestra. Coral de marca
                      y no rojo crudo: es una anotación del anfitrión o del
                      huésped, no un error del sistema. */}
                  {photo.isDamage && (
                    <View
                      style={{
                        position: 'absolute',
                        top: SPACING.xs,
                        left: SPACING.xs,
                        backgroundColor: COLORS.coral.DEFAULT,
                        borderRadius: RADIUS.sm,
                        paddingHorizontal: SPACING.xs,
                        paddingVertical: 2,
                      }}
                      testID={`${testIDPrefix}.damage-${area}`}
                    >
                      <Text
                        style={{ fontFamily: FONTS.body, fontSize: 12, color: '#FFFFFF' }}
                        numberOfLines={1}
                      >
                        {t('inspection.damage_marked')}
                      </Text>
                    </View>
                  )}
                  {/* Retake overlay */}
                  <View
                    style={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      backgroundColor: 'rgba(0,0,0,0.5)',
                      flexDirection: 'row',
                      alignItems: 'center',
                      justifyContent: 'center',
                      paddingVertical: SPACING.xs,
                      gap: SPACING.xs,
                    }}
                  >
                    <RefreshCw size={14} color="#FFFFFF" />
                    <Text style={{ fontFamily: FONTS.body, fontSize: 12, color: '#FFFFFF' }}>
                      {t('checkin.retake_photo')} - {label}
                    </Text>
                  </View>
                </View>
              ) : (
                <View style={{ alignItems: 'center', gap: SPACING.xs }}>
                  <Camera size={24} color={colors.textMuted} />
                  <Text style={{ fontFamily: FONTS.body, fontSize: 12, color: colors.textMuted }}>
                    {label}
                  </Text>
                  {!isRequired && (
                    <Text
                      style={{ fontFamily: FONTS.body, fontSize: 12, color: colors.textMuted }}
                      testID={`${testIDPrefix}.optional-${area}`}
                    >
                      {t('checkin.photo_optional')}
                    </Text>
                  )}
                </View>
              )}
            </Pressable>
          )
        })}
        {onToggleDamage && (
          <Text
            style={{
              fontFamily: FONTS.body,
              fontSize: 13,
              lineHeight: 18,
              color: colors.textSecondary,
            }}
          >
            {t('inspection.damage_hint')}
          </Text>
        )}
      </View>
      <InspectionLiveCamera
        visible={activeArea !== null}
        area={activeArea}
        areaLabel={activeArea ? t(AREA_LABEL_KEYS[activeArea]) : ''}
        onCancel={() => setActiveArea(null)}
        onCapture={(photo) => {
          onPhotoTaken(photo)
          setActiveArea(null)
        }}
      />
    </View>
  )
}
